import PMBInputNumber from 'components/PMBInputNumber'
import PMBSelect from 'components/PMBSelect'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ChevronDown,
  ChevronUp,
  Layers,
  Plus,
  Trash2,
} from 'react-feather'
import { useDispatch } from 'react-redux'
import { useSnackbar } from 'notistack'
import { Button, Spinner } from 'reactstrap'
import { formatInputNumber } from 'utility/Utils'
import { newFormatThousandSeparator } from 'utils/formatNumber'
import NoImage from 'assets/images/noimage.png'
import { splitToManyInventory } from 'features/public-views/manageStock/API/manageStockAction'
import { useFetchFindoraInventoryListQuery } from './productMappingSchemaListApiSlice'

import ModalConfirmAutoPopulate from './ModalConfirmAutoPopulate'

const RecipeItem = ({
  recipe,
  index,
  onAmountChange,
  onInventoryChange,
  onAddEntry,
  onRemoveEntry,
  onSetEntries,
  quantity,
  status,
  usedInventoryIds = [],
  activeWarehouse = null,
  isInvalid = false,
}) => {
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()

  const [isAutoPopulating, setIsAutoPopulating] = useState(false)
  const [splitPlan, setSplitPlan] = useState(null)
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [isSubmittingSplit, setIsSubmittingSplit] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(false)

  const materialItemId = recipe.itemId || recipe.item?.id || recipe.id

  const {
    data: findoraInventoryList,
    isLoading: isLoadingInventory,
    refetch: refetchInventory,
  } = useFetchFindoraInventoryListQuery(
    {
      itemIds: materialItemId,
    },
    {
      skip: !materialItemId,
    },
  )

  const getWarehouseId = useCallback((inv) => {
    return inv?.warehouseId ?? inv?.customLabel?.warehouseId ?? null
  }, [])

  const getWarehouseName = useCallback((inv) => {
    return inv?.warehouseName || inv?.customLabel?.warehouseName || '-'
  }, [])

  const matchesWarehouse = useCallback(
    (inv, targetWarehouse) => {
      if (!targetWarehouse) return true
      const targetId =
        typeof targetWarehouse === 'object'
          ? targetWarehouse?.id
          : targetWarehouse
      const targetName =
        typeof targetWarehouse === 'object'
          ? targetWarehouse?.name
          : targetWarehouse

      const invId = getWarehouseId(inv)
      const invName = getWarehouseName(inv)

      if (targetId && invId) {
        if (
          String(invId).trim().toLowerCase() ===
          String(targetId).trim().toLowerCase()
        ) {
          return true
        }
      }
      if (targetName && invName && invName !== '-') {
        if (
          String(invName).trim().toLowerCase() ===
          String(targetName).trim().toLowerCase()
        ) {
          return true
        }
      }
      return false
    },
    [getWarehouseId, getWarehouseName],
  )

  const inventoryOptions = useMemo(() => {
    if (!findoraInventoryList || !Array.isArray(findoraInventoryList)) {
      return []
    }

    const mapped = findoraInventoryList.map((inventory) => {
      const itemName =
        inventory.item?.nameItem ||
        inventory.nameItem ||
        recipe.nameItem ||
        '-'
      const ownerCode = inventory.ownerCode || '-'
      const lotCode = inventory.lotCode || '-'
      const amount = inventory.amount || 0
      const unit = inventory.unit || recipe.unit || '-'
      const warehouseName = inventory.warehouseName || '-'
      const groupName = inventory.groupLabel || '-'

      const label = `${itemName} | ID: ${inventory.id} | Kode Inventori: ${ownerCode} | Lot ID: ${lotCode} | ${amount} ${unit} | Gudang: ${warehouseName} | Grup: ${groupName}`

      return {
        value: inventory.id,
        label: label,
        image: inventory.item?.linkFoto || inventory.linkFoto,
        customLabel: {
          id: inventory.id,
          itemName,
          ownerCode,
          lotCode,
          amount,
          unit,
          warehouseId: inventory.warehouseId,
          warehouseName: inventory.warehouseName,
          groupName,
        },
        ...inventory,
      }
    })

    // Filter options matching recipe unit if available
    const recipeUnit = recipe.unit
    let filtered = mapped
    if (recipeUnit) {
      const unitLower = String(recipeUnit).toLowerCase().trim()
      filtered = mapped.filter((opt) => {
        const optUnit = String(
          opt.unit || opt.customLabel?.unit || '',
        )
          .toLowerCase()
          .trim()
        return optUnit === unitLower
      })
    }

    // Sort options: If activeWarehouse exists, prioritize matching warehouse, then by ID descending
    filtered.sort((a, b) => {
      if (activeWarehouse) {
        const aMatches = matchesWarehouse(a, activeWarehouse)
        const bMatches = matchesWarehouse(b, activeWarehouse)
        if (aMatches && !bMatches) return -1
        if (!aMatches && bMatches) return 1
      }
      return (Number(b.id) || 0) - (Number(a.id) || 0)
    })

    return filtered.length > 0 ? filtered : mapped
  }, [
    findoraInventoryList,
    recipe.nameItem,
    recipe.unit,
    activeWarehouse,
    matchesWarehouse,
  ])

  // Helper to build formatted option from raw inventory object
  const buildOption = useCallback(
    (inventory) => {
      const itemName =
        inventory.item?.nameItem ||
        inventory.nameItem ||
        recipe.nameItem ||
        '-'
      const ownerCode = inventory.ownerCode || '-'
      const lotCode = inventory.lotCode || '-'
      const amount = inventory.amount || inventory.amountNum || 0
      const unit = inventory.unit || recipe.unit || 'Kg'
      const warehouseName = inventory.warehouseName || '-'
      const groupName = inventory.groupLabel || '-'

      const label = `${itemName} | ID: ${inventory.id} | Kode Inventori: ${ownerCode} | Lot ID: ${lotCode} | ${amount} ${unit} | Gudang: ${warehouseName} | Grup: ${groupName}`

      return {
        value: inventory.id,
        label: label,
        image:
          inventory.item?.linkFoto ||
          inventory.linkFoto ||
          recipe.image ||
          NoImage,
        customLabel: {
          id: inventory.id,
          itemName,
          ownerCode,
          lotCode,
          amount,
          unit,
          warehouseId: inventory.warehouseId,
          warehouseName: inventory.warehouseName,
          groupName,
        },
        ...inventory,
      }
    },
    [recipe.nameItem, recipe.unit, recipe.image],
  )

  // AUTO SELECT INVENTORY IF ONLY ONE OPTION AVAILABLE & ONLY 1 UNFILLED ENTRY
  useEffect(() => {
    if (
      inventoryOptions &&
      inventoryOptions.length === 1 &&
      recipe?.entries?.length === 1 &&
      !recipe.entries[0].inventoryId
    ) {
      onInventoryChange(index, recipe.entries[0].id, inventoryOptions[0])
    }
  }, [inventoryOptions, recipe.entries, onInventoryChange, index])

  // Helper: Find exact subset combination of intact inventories summing to target
  const findExactSubset = (items, target, tolerance = 0.0001) => {
    const sorted = [...items].sort((a, b) => b.amountNum - a.amountNum)

    const search = (itemIndex, currentSum, selected) => {
      if (Math.abs(currentSum - target) <= tolerance) {
        return selected
      }
      if (currentSum > target + tolerance || itemIndex >= sorted.length) {
        return null
      }

      // Include current
      const withCurrent = search(
        itemIndex + 1,
        currentSum + sorted[itemIndex].amountNum,
        [...selected, sorted[itemIndex]],
      )
      if (withCurrent) return withCurrent

      // Exclude current
      return search(itemIndex + 1, currentSum, selected)
    }

    return search(0, 0, [])
  }

  const triggerSplitModal = ({
    type,
    targetInv,
    splitAmount,
    remainderAmount,
    intactEntries = [],
    requiredAmount,
  }) => {
    setSplitPlan({
      type,
      recipeName: recipe.nameItem,
      requiredAmount,
      unit: recipe.unit || 'Kg',
      targetInv,
      splitAmount,
      remainderAmount,
      intactInvs: intactEntries.map((e) => e.inventoryId),
      execute: async () => {
        const payload = {
          inventoryId: targetInv.id,
          arrNew: [
            {
              amount: splitAmount,
              itemId: targetInv.itemId || materialItemId,
              unit: targetInv.unit || recipe.unit || 'Kg',
              ...(targetInv?.bundle && {
                bundle: Number(targetInv.bundle),
              }),
            },
            {
              amount: remainderAmount,
              itemId: targetInv.itemId || materialItemId,
              unit: targetInv.unit || recipe.unit || 'Kg',
              ...(targetInv?.bundle && {
                bundle: Number(targetInv.bundle),
              }),
            },
          ],
          notes: `auto-split-pemetaan-${targetInv.id}-${Date.now()}`,
        }

        const splitResult = await dispatch(splitToManyInventory(payload)).unwrap()
        const newInventories = Array.isArray(splitResult)
          ? splitResult
          : splitResult?.items ||
            splitResult?.data ||
            splitResult?.newInventories ||
            splitResult?.result ||
            []

        if (newInventories && newInventories.length > 0) {
          const selectedSplitInv = newInventories[0]
          const finalEntries = [
            ...intactEntries,
            {
              id: Date.now() + Math.random(),
              inventoryId: buildOption({
                ...targetInv,
                ...selectedSplitInv,
              }),
              amount: splitAmount,
            },
          ]
          setIsCollapsed(finalEntries.length > 3)
          onSetEntries(index, finalEntries)
          if (refetchInventory) await refetchInventory()
          enqueueSnackbar(
            `Inventori berhasil dipisah (${splitAmount} ${recipe.unit || 'Kg'} & sisa ${remainderAmount} ${recipe.unit || 'Kg'} - Gudang: ${getWarehouseName(targetInv)}) dan dipilih.`,
            { variant: 'success' },
          )
        }
      },
    })
    setIsConfirmModalOpen(true)
  }

  // AUTO POPULATE ALGORITHM (Strict Same-Unit & Same-Warehouse Optimization)
  const handleAutoPopulate = async () => {
    try {
      setIsAutoPopulating(true)

      // Follow what the user set in Alokasi; fall back to Dibutuhkan only when nothing is allocated
      const targetRequiredAmount = totalAllocated > 0 ? totalAllocated : neededAmount

      const requiredAmount = Number(targetRequiredAmount)

      if (requiredAmount <= 0) {
        enqueueSnackbar('Jumlah yang dibutuhkan tidak valid (0).', {
          variant: 'warning',
        })
        return
      }

      // Refresh and get latest inventory list
      let latestInventoryList = findoraInventoryList
      if (refetchInventory) {
        const refetchRes = await refetchInventory()
        if (refetchRes?.data) {
          latestInventoryList = refetchRes.data
        }
      }

      if (
        !latestInventoryList ||
        !Array.isArray(latestInventoryList) ||
        latestInventoryList.length === 0
      ) {
        enqueueSnackbar(
          'Tidak ada stok inventori yang tersedia untuk material ini.',
          { variant: 'warning' },
        )
        return
      }

      // 1. FILTER BY UNIT (Strict Unit Matching)
      const targetUnit = String(recipe.unit || 'Kg')
        .trim()
        .toLowerCase()

      const unitMatchingInvs = latestInventoryList
        .filter((inv) => {
          const invUnit = String(
            inv.unit || inv.customLabel?.unit || '',
          )
            .trim()
            .toLowerCase()
          return (
            invUnit === targetUnit &&
            Number(inv.amount || 0) > 0 &&
            !usedInventoryIds.includes(inv.id)
          )
        })
        .map((inv) => ({
          ...inv,
          amountNum: Number(inv.amount || 0),
        }))

      if (unitMatchingInvs.length === 0) {
        enqueueSnackbar(
          `Tidak ada stok inventori dengan satuan "${recipe.unit || 'Kg'}" yang tersedia untuk material ini.`,
          { variant: 'warning' },
        )
        return
      }

      const TOLERANCE = 0.0001
      const roundNum = (num) =>
        Math.round((Number(num) + Number.EPSILON) * 1000) / 1000

      // 2. WAREHOUSE FILTERING & GROUPING (by ID and Name)
      const warehouseGroups = {}
      if (activeWarehouse) {
        const matchingInvs = unitMatchingInvs.filter((inv) =>
          matchesWarehouse(inv, activeWarehouse),
        )

        if (matchingInvs.length === 0) {
          const whDisplayName =
            (typeof activeWarehouse === 'object'
              ? activeWarehouse?.name || activeWarehouse?.id
              : activeWarehouse) || 'terpilih'
          enqueueSnackbar(
            `Tidak ada stok inventori di gudang "${whDisplayName}" dengan satuan "${recipe.unit || 'Kg'}". Semua material harus berasal dari gudang yang sama.`,
            { variant: 'warning' },
          )
          return
        }

        const groupKey =
          (typeof activeWarehouse === 'object'
            ? activeWarehouse?.id || activeWarehouse?.name
            : activeWarehouse) || 'activeWarehouse'
        warehouseGroups[groupKey] = matchingInvs
      } else {
        unitMatchingInvs.forEach((inv) => {
          const wh = getWarehouseId(inv) || getWarehouseName(inv)
          if (!warehouseGroups[wh]) {
            warehouseGroups[wh] = []
          }
          warehouseGroups[wh].push(inv)
        })
      }

      // 3. RUN MINIMUM-OPERATIONS ALGORITHM PER WAREHOUSE
      // Evaluate best candidate warehouse solution
      let bestSolution = null

      const warehouseKeys = Object.keys(warehouseGroups)
      for (const whKey of warehouseKeys) {
        const whInvs = warehouseGroups[whKey]

        // 3a. Exact Single Match (0 splits)
        const exactMatch = whInvs.find(
          (inv) => Math.abs(inv.amountNum - requiredAmount) <= TOLERANCE,
        )
        if (exactMatch) {
          bestSolution = {
            type: 'EXACT_SINGLE',
            inventory: exactMatch,
            priority: 1,
          }
          break // Best possible outcome, stop searching
        }

        // 3b. Exact Subset Combination within this warehouse (0 splits)
        const exactSubset = findExactSubset(whInvs, requiredAmount, TOLERANCE)
        if (exactSubset && exactSubset.length > 0) {
          if (!bestSolution || bestSolution.priority > 2) {
            bestSolution = {
              type: 'EXACT_SUBSET',
              subset: exactSubset,
              priority: 2,
            }
          }
        }

        // 3c. Single Larger Split within this warehouse (1 split)
        const largerInvs = whInvs
          .filter((inv) => inv.amountNum > requiredAmount + TOLERANCE)
          .sort((a, b) => a.amountNum - b.amountNum)

        if (largerInvs.length > 0) {
          const targetInv = largerInvs[0]
          const remainder = roundNum(targetInv.amountNum - requiredAmount)
          if (!bestSolution || bestSolution.priority > 3) {
            bestSolution = {
              type: 'SINGLE_SPLIT',
              targetInv,
              remainder,
              priority: 3,
            }
          }
        }

        // 3d. Greedy combination with at most 1 split within this warehouse
        const sortedDesc = [...whInvs].sort((a, b) => b.amountNum - a.amountNum)
        let collectedAmount = 0
        const entriesToSet = []
        const intactInvsForPlan = []
        let splitInfo = null

        for (let i = 0; i < sortedDesc.length; i++) {
          const inv = sortedDesc[i]
          const neededRemaining = roundNum(requiredAmount - collectedAmount)

          if (neededRemaining <= TOLERANCE) break

          if (inv.amountNum <= neededRemaining + TOLERANCE) {
            entriesToSet.push({
              id: Date.now() + Math.random() + i,
              inventoryId: buildOption(inv),
              amount: inv.amountNum,
            })
            intactInvsForPlan.push(inv)
            collectedAmount = roundNum(collectedAmount + inv.amountNum)
          } else {
            const remainder = roundNum(inv.amountNum - neededRemaining)
            splitInfo = {
              targetInv: inv,
              splitAmount: neededRemaining,
              remainderAmount: remainder,
              intactInvs: intactInvsForPlan,
              existingEntries: entriesToSet,
            }
            collectedAmount = roundNum(collectedAmount + neededRemaining)
            break
          }
        }

        if (splitInfo && (!bestSolution || bestSolution.priority > 4)) {
          bestSolution = {
            type: 'COMBINE_SPLIT',
            splitInfo,
            priority: 4,
          }
        } else if (
          !bestSolution &&
          entriesToSet.length > 0 &&
          collectedAmount > 0
        ) {
          bestSolution = {
            type: 'INSUFFICIENT_COMBINE',
            entriesToSet,
            collectedAmount,
            priority: 5,
          }
        }
      }

      // 4. EXECUTE BEST SOLUTION
      if (!bestSolution) {
        enqueueSnackbar('Gagal menemukan inventori yang cocok.', {
          variant: 'error',
        })
        return
      }

      if (bestSolution.type === 'EXACT_SINGLE') {
        setIsCollapsed(false)
        onSetEntries(index, [
          {
            id: Date.now() + Math.random(),
            inventoryId: buildOption(bestSolution.inventory),
            amount: requiredAmount,
          },
        ])
        enqueueSnackbar(
          `Berhasil memilih inventori #${bestSolution.inventory.id} (${requiredAmount} ${recipe.unit || 'Kg'} - Gudang: ${getWarehouseName(bestSolution.inventory)}).`,
          { variant: 'success' },
        )
        return
      }

      if (bestSolution.type === 'EXACT_SUBSET') {
        const newEntries = bestSolution.subset.map((inv, idx) => ({
          id: Date.now() + Math.random() + idx,
          inventoryId: buildOption(inv),
          amount: inv.amountNum,
        }))
        setIsCollapsed(newEntries.length > 3)
        onSetEntries(index, newEntries)
        enqueueSnackbar(
          `Berhasil memilih ${bestSolution.subset.length} inventori dengan total ${requiredAmount} ${recipe.unit || 'Kg'} (Gudang: ${getWarehouseName(bestSolution.subset[0])}).`,
          { variant: 'success' },
        )
        return
      }

      if (bestSolution.type === 'SINGLE_SPLIT') {
        triggerSplitModal({
          type: 'SPLIT',
          targetInv: bestSolution.targetInv,
          splitAmount: requiredAmount,
          remainderAmount: bestSolution.remainder,
          intactEntries: [],
          requiredAmount,
        })
        return
      }

      if (bestSolution.type === 'COMBINE_SPLIT') {
        const { targetInv, splitAmount, remainderAmount, existingEntries } =
          bestSolution.splitInfo
        triggerSplitModal({
          type: 'COMBINE_AND_SPLIT',
          targetInv,
          splitAmount,
          remainderAmount,
          intactEntries: existingEntries,
          requiredAmount,
        })
        return
      }

      if (bestSolution.type === 'INSUFFICIENT_COMBINE') {
        const { entriesToSet, collectedAmount } = bestSolution
        setIsCollapsed(entriesToSet.length > 3)
        onSetEntries(index, entriesToSet)
        if (refetchInventory) await refetchInventory()

        enqueueSnackbar(
          `Stok inventori di gudang hanya mencukupi ${collectedAmount} ${recipe.unit || 'Kg'} dari ${requiredAmount} ${recipe.unit || 'Kg'} yang dibutuhkan.`,
          { variant: 'warning' },
        )
      }
    } catch (error) {
      console.error('Auto populate error:', error)
      enqueueSnackbar(
        error?.message || error || 'Gagal melakukan auto populate inventori.',
        { variant: 'error' },
      )
    } finally {
      setIsAutoPopulating(false)
    }
  }

  // Handle execution when user confirms in modal
  const handleConfirmSplit = async () => {
    if (!splitPlan?.execute) return
    try {
      setIsSubmittingSplit(true)
      await splitPlan.execute()
      setIsConfirmModalOpen(false)
      setSplitPlan(null)
    } catch (error) {
      console.error('Error executing split:', error)
      enqueueSnackbar(
        error?.message || error || 'Gagal memisahkan inventori.',
        { variant: 'error' },
      )
    } finally {
      setIsSubmittingSplit(false)
    }
  }

  // What this material needs (for a substitute: the part the other substitutes have not covered yet)
  const neededAmount = Number(
    recipe?.requiredAmount ?? Number(recipe?.reqAmount || 0) * Number(quantity || 0),
  )

  const entries = useMemo(() => {
    const stored = recipe?.entries || [
      { id: 1, inventoryId: recipe.inventoryId || null, amount: neededAmount },
    ]
    // An untouched substitute's default Alokasi follows its (shared) Dibutuhkan; once the user types
    // another amount (it no longer equals the recipe's own default), their value wins
    const ownDefault = Number(recipe?.reqAmount || 0) * Number(quantity || 0)
    const untouched = Math.abs((Number(stored[0]?.amount) || 0) - ownDefault) < 0.0005
    if (recipe?.requiredAmount !== undefined && stored.length === 1 && !stored[0].inventoryId && untouched) {
      return [{ ...stored[0], amount: neededAmount }]
    }
    return stored
  }, [recipe?.entries, recipe.inventoryId, recipe?.requiredAmount, recipe?.reqAmount, quantity, neededAmount])

  const totalAllocated = useMemo(() => {
    return entries.reduce((sum, e) => sum + (Number(e.amount) || 0), 0)
  }, [entries])

  // Picking a batch keeps the shown Alokasi (stored, since a substitute's default is only derived);
  // an empty row from "+ Tambah" gets what is still needed, capped by the batch's stock
  const handlePickInventory = (entry, inventory) => {
    onInventoryChange(index, entry.id, inventory)
    if (!inventory) return
    const shown = Number(entry.amount) || 0
    if (shown > 0) {
      const stored = Number(recipe?.entries?.find((e) => e.id === entry.id)?.amount) || 0
      if (Math.abs(shown - stored) > 0.0005) {
        onAmountChange(index, entry.id, newFormatThousandSeparator(shown, 3))
      }
      return
    }
    const stock = Number(inventory.amount || inventory.customLabel?.amount || 0)
    const otherAllocated = entries
      .filter((e) => e.id !== entry.id && e.inventoryId)
      .reduce((sum, e) => sum + (Number(e.amount) || 0), 0)
    const fill = Math.max(Math.min(neededAmount - otherAllocated, stock > 0 ? stock : Infinity), 0)
    if (fill > 0) onAmountChange(index, entry.id, newFormatThousandSeparator(fill, 3))
  }

  const totalSelectedStock = useMemo(() => {
    return entries.reduce((sum, entry) => {
      if (!entry.inventoryId) return sum
      const invStock = Number(
        entry.inventoryId?.amount ||
        entry.inventoryId?.customLabel?.amount ||
        0,
      )
      return sum + invStock
    }, 0)
  }, [entries])

  const hasInsufficientStock = useMemo(() => {
    let hasAnyInventorySelected = false
    let isAnyEntryExceeding = false

    entries.forEach((entry) => {
      if (entry.inventoryId) {
        hasAnyInventorySelected = true
        const invStock = Number(
          entry.inventoryId?.amount ||
          entry.inventoryId?.customLabel?.amount ||
          0,
        )
        // Gunakan toleransi (0.001) untuk menghindari floating-point glitch (misal 4.2 vs 4.199999999999999)
        if (Number(entry.amount) - invStock > 0.001) {
          isAnyEntryExceeding = true
        }
      }
    })

    if (!hasAnyInventorySelected) return false

    return isAnyEntryExceeding || totalAllocated - totalSelectedStock > 0.001
  }, [entries, totalAllocated, totalSelectedStock])

  const getIsOptionDisabledForEntry = useCallback(
    (entryId) => (option) => {
      const optionId = option.value || option.id
      // Allow current option if it's already selected in this entry
      const currentEntry = entries.find((e) => e.id === entryId)
      if (
        currentEntry?.inventoryId?.value === optionId ||
        currentEntry?.inventoryId?.id === optionId
      ) {
        return false
      }

      // Disable if selected in other entries of this material
      const isUsedInThisRecipe = entries.some(
        (e) =>
          e.id !== entryId &&
          (e.inventoryId?.value === optionId || e.inventoryId?.id === optionId),
      )

      // Disable if selected in other materials
      const isUsedInOtherRecipes = usedInventoryIds.includes(optionId)

      return isUsedInThisRecipe || isUsedInOtherRecipes
    },
    [entries, usedInventoryIds],
  )

  const renderOptionLabelForEntry = useCallback(
    (entryId) => (option) => {
      if (!option.customLabel) return option.label || 'Unknown Inventory'

      const { id, lotCode, amount, unit, warehouseName, groupName } =
        option.customLabel

      const isOptionDisabled = getIsOptionDisabledForEntry(entryId)(option)

      return (
        <div
          className={`d-flex flex-column py-1 w-100 ${isOptionDisabled ? 'opacity-50' : ''}`}
          style={{ cursor: isOptionDisabled ? 'not-allowed' : 'pointer' }}
        >
          <div className="d-flex justify-content-between align-items-start">
            <div className="d-flex flex-column">
              <div
                className={`fw-bold ${isOptionDisabled ? 'text-muted' : 'text-dark'}`}
                style={{ fontSize: '0.85rem', lineHeight: '1.2' }}
              >
                {`Lot ID: ${lotCode ?? '-'}`}
                <span
                  className="text-muted fw-normal ms-1"
                  style={{ fontSize: '0.75rem' }}
                >
                  #{id || option.value}
                </span>
                {isOptionDisabled && (
                  <span
                    className="badge bg-light text-muted ms-1 border"
                    style={{ fontSize: '0.65rem' }}
                  >
                    Sudah Dipilih
                  </span>
                )}
              </div>
              <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                {warehouseName}
                {groupName && groupName !== '-' ? ` | ${groupName}` : ''}
              </div>
            </div>
            <div
              className={`fw-bold text-end ${isOptionDisabled ? 'text-muted' : 'text-primary'}`}
              style={{ fontSize: '0.85rem' }}
            >
              {newFormatThousandSeparator(amount, 3)} {unit}
            </div>
          </div>
        </div>
      )
    },
    [getIsOptionDisabledForEntry],
  )

  return (
    <div
      className="mb-3 border rounded-3 bg-white overflow-hidden shadow-sm transition-all"
      style={{
        borderLeft: hasInsufficientStock ? '4px solid #ff9f43' : undefined,
      }}
    >
      {/* Header Material: Clean Compact Row with Auto-Populate Button */}
      <div className="p-2 p-md-3 border-bottom border-light bg-light bg-opacity-10">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <div
              className="bg-white rounded d-flex align-items-center justify-content-center overflow-hidden border"
              style={{ width: '36px', height: '36px' }}
            >
              <img
                src={recipe.image || NoImage}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
            <div
              className="d-flex flex-column justify-content-center"
              style={{ gap: '4px' }}
            >
              <div
                className="fw-bold text-dark"
                style={{ fontSize: '0.9rem', lineHeight: '1.4' }}
              >
                {recipe.nameItem}
              </div>
              <div
                className="text-muted"
                style={{ fontSize: '0.7rem', lineHeight: '1.2' }}
              >
                Bahan Baku Utama
              </div>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2 gap-md-3">
            {/* Auto Populate Button */}
            <Button
              color="primary"
              outline
              size="sm"
              data-testid={`product-mapping-schema-autopopulate-button-${recipe.id}`}
              disabled={isAutoPopulating || isLoadingInventory}
              onClick={handleAutoPopulate}
              className="d-flex align-items-center fw-semibold text-nowrap py-1 px-2"
              style={{
                borderRadius: '6px',
                fontSize: '0.75rem',
              }}
            >
              {isAutoPopulating ? (
                <>
                  <Spinner
                    size="sm"
                    className="me-1"
                    style={{ width: '12px', height: '12px' }}
                  />
                  Memproses...
                </>
              ) : (
                <>
                  Auto Populate
                </>
              )}
            </Button>

            <div
              className="text-end d-flex flex-column justify-content-center"
              style={{ gap: '4px' }}
            >
              <div
                className="text-muted text-uppercase fw-bold"
                style={{
                  fontSize: '0.65rem',
                  letterSpacing: '0.8px',
                  lineHeight: '1.2',
                }}
              >
                Dibutuhkan
              </div>
              <div
                className="fw-bold text-primary"
                style={{ fontSize: '1.1rem', lineHeight: '1.2' }}
              >
                {newFormatThousandSeparator(neededAmount, 3)}{' '}
                {recipe?.unit || 'Kg'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Inventory Selection Area */}
      <div className="p-2 p-md-3 bg-white">
        {/* COLLAPSED VIEW (When many entries are selected and collapsed) */}
        {entries.length > 2 && isCollapsed ? (
          <div
            className="p-3 rounded-3 border"
            style={{ backgroundColor: '#f8fafc', borderColor: '#e2e8f0' }}
          >
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="d-flex align-items-center gap-2">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    width: '26px',
                    height: '26px',
                    backgroundColor: '#e6fffa',
                    color: '#009688',
                  }}
                >
                  <Layers size={14} />
                </div>
                <div>
                  <span className="fw-bold text-dark" style={{ fontSize: '0.85rem' }}>
                    {entries.length} Batch Inventori Terpilih
                  </span>
                  <span
                    className="badge rounded-pill ms-2"
                    style={{
                      backgroundColor: '#e6f4ea',
                      color: '#137333',
                      fontSize: '0.75rem',
                    }}
                  >
                    Total: {newFormatThousandSeparator(totalAllocated, 3)} {recipe.unit || 'Kg'}
                  </span>
                </div>
              </div>

              <Button
                color="link"
                size="sm"
                className="p-0 text-primary d-flex align-items-center text-decoration-none fw-semibold"
                style={{ fontSize: '0.8rem' }}
                onClick={() => setIsCollapsed(false)}
                data-testid={`expand-entries-btn-${recipe.id}`}
              >
                Lihat Detail Form ({entries.length}) <ChevronDown size={14} className="ms-1" />
              </Button>
            </div>

            {/* Compact list of selected batches */}
            <div
              className="d-flex flex-column gap-1 overflow-y-auto mt-2 pe-1"
              style={{ maxHeight: '160px' }}
            >
              {entries.map((entry, idx) => {
                const inv = entry.inventoryId?.customLabel || entry.inventoryId
                return (
                  <div
                    key={entry.id || idx}
                    className="d-flex justify-content-between align-items-center px-2 py-1 bg-white rounded border border-light"
                    style={{ fontSize: '0.78rem' }}
                  >
                    <div className="d-flex align-items-center gap-2 text-truncate me-2">
                      <span className="text-muted fw-bold">#{idx + 1}</span>
                      <span className="fw-semibold text-dark text-truncate">
                        {inv?.lotCode && inv.lotCode !== '-'
                          ? `Lot ID: ${inv.lotCode}`
                          : `Kode: ${inv?.ownerCode || '-'}`}
                        <span className="text-muted ms-1 text-xs">
                          (#{entry.inventoryId?.id || entry.inventoryId?.value || '-'})
                        </span>
                      </span>
                      <span className="text-muted text-xs">
                        {inv?.warehouseName ? `| ${inv.warehouseName}` : ''}
                      </span>
                    </div>
                    <div className="fw-bold text-primary text-nowrap">
                      {newFormatThousandSeparator(entry.amount, 3)} {recipe.unit || 'Kg'}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ) : (
          /* EXPANDED FULL FORM VIEW */
          <div className="d-flex flex-column gap-3">
            {entries.length > 2 && (
              <div className="d-flex justify-content-between align-items-center px-1 pb-1 border-bottom border-light">
                <span className="text-muted fw-bold" style={{ fontSize: '0.75rem' }}>
                  Menampilkan {entries.length} formulir inventori:
                </span>
                <Button
                  color="link"
                  size="sm"
                  className="p-0 text-primary d-flex align-items-center text-decoration-none fw-semibold"
                  style={{ fontSize: '0.75rem' }}
                  onClick={() => setIsCollapsed(true)}
                  data-testid={`collapse-entries-btn-${recipe.id}`}
                >
                  Sembunyikan Form <ChevronUp size={14} className="ms-1" />
                </Button>
              </div>
            )}

            <div
              className="d-flex flex-column gap-3 overflow-y-auto"
              style={{
                maxHeight: entries.length > 3 ? '400px' : 'none',
                paddingRight: entries.length > 3 ? '4px' : '0',
              }}
            >
              {entries.map((entry, entryIdx) => (
                <div
                  key={entry.id || entryIdx}
                  className="d-flex flex-column gap-3 pb-3 border-bottom border-light last-border-0 position-relative"
                >
                  {entries.length > 1 && (
                    <Button
                      color="flat-danger"
                      data-testid={`product-mapping-schema-remove-entry-${recipe.id}-${entry.id}`}
                      className="p-0 position-absolute d-flex align-items-center justify-content-center"
                      onClick={() => onRemoveEntry(index, entry.id)}
                      style={{
                        width: '28px',
                        height: '28px',
                        right: '-4px',
                        top: '-4px',
                        backgroundColor: '#fff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        borderRadius: '50%',
                        zIndex: 1,
                        border: '1px solid #fee2e2',
                      }}
                    >
                      <Trash2 size={13} color="#ef4444" />
                    </Button>
                  )}
                  <div className="w-100">
                    <label
                      className="form-label text-dark mb-2"
                      style={{
                        fontSize: '0.8rem',
                        fontWeight: '700',
                        letterSpacing: '0.3px',
                      }}
                    >
                      SUMBER INVENTORI {entries.length > 1 ? `#${entryIdx + 1}` : ''}
                    </label>
                    <PMBSelect
                      value={entry.inventoryId}
                      options={inventoryOptions}
                      isOptionDisabled={getIsOptionDisabledForEntry(entry.id)}
                      onChange={(selectedInventory) => handlePickInventory(entry, selectedInventory)}
                      getOptionLabel={(e) => e.label}
                      formatOptionLabel={renderOptionLabelForEntry(entry.id)}
                      name={`itemInventory-${index}-${entryIdx}`}
                      placeholder={
                        isLoadingInventory
                          ? 'Memuat inventori...'
                          : 'Pilih batch inventori...'
                      }
                      isDisabled={isLoadingInventory || isAutoPopulating}
                      isLoading={isLoadingInventory}
                      menuPortalTarget={document.body}
                      styles={{
                        control: (base) => {
                          // Only empty entries are what's missing; filled ones stay neutral
                          const border = isInvalid && !entry.inventoryId ? '#dc3545' : '#D0D5DD'
                          return {
                            ...base,
                            borderColor: border,
                            '&:hover': { borderColor: border },
                            borderRadius: '8px',
                            boxShadow: 'none',
                            minHeight: '42px',
                            backgroundColor: '#fff',
                          }
                        },
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                  </div>
                  <div className="w-100">
                    <label
                      className="form-label text-dark mb-2"
                      style={{
                        fontSize: '0.8rem',
                        fontWeight: '700',
                        letterSpacing: '0.3px',
                      }}
                    >
                      ALOKASI
                    </label>
                    <PMBInputNumber
                      onFocus={(e) => e.target.select()}
                      value={formatInputNumber(entry.amount)}
                      onChange={(e) =>
                        onAmountChange(index, entry.id, e.target.value)
                      }
                      placeholder="0"
                      prefix={recipe.unit || 'Kg'}
                      isLastPrefix
                      style={{
                        height: '42px',
                        backgroundColor: '#fff',
                        borderColor: '#D0D5DD',
                        fontSize: '1rem',
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Card Footer: Status Info & Add Entry Button */}
      <div className="px-2 px-md-3 py-2 bg-light bg-opacity-5 d-flex justify-content-between align-items-center border-top border-light flex-wrap gap-2">
        {hasInsufficientStock ? (
          <div className="d-flex align-items-center gap-2 py-1">
            <div
              className="rounded-circle"
              style={{
                width: '10px',
                height: '10px',
                backgroundColor: '#ff9f43',
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontSize: '0.8rem',
                fontWeight: '600',
                color: '#C2410C',
                lineHeight: '1.4',
              }}
            >
              Stok inventori tidak mencukupi ({newFormatThousandSeparator(totalSelectedStock, 3)} / {newFormatThousandSeparator(totalAllocated, 3)} {recipe.unit || 'Kg'}). Klik <strong>+ Tambah</strong> atau <strong>Auto Populate</strong>.
            </span>
          </div>
        ) : (
          <div />
        )}

        <Button
          color="primary"
          outline
          size="sm"
          data-testid={`product-mapping-schema-add-entry-${recipe.id}`}
          className="d-flex align-items-center gap-1 px-3 fw-bold ms-auto"
          style={{ borderRadius: '8px', height: '32px', fontSize: '0.8rem' }}
          onClick={() => {
            setIsCollapsed(false)
            onAddEntry(index)
          }}
        >
          <Plus size={14} />
          Tambah
        </Button>
      </div>

      {/* Confirmation Modal for Auto Populate Split Action */}
      <ModalConfirmAutoPopulate
        isOpen={isConfirmModalOpen}
        toggle={() => {
          if (!isSubmittingSplit) {
            setIsConfirmModalOpen(false)
            setSplitPlan(null)
          }
        }}
        isLoading={isSubmittingSplit}
        onConfirm={handleConfirmSplit}
        plan={splitPlan}
        neededAmount={neededAmount}
      />
    </div>
  )
}

export default RecipeItem
